import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { Panel, PanelBody } from './../../components/panel/panel.js';
import { Button, Card, Col, Form, Input, Row, Space, Switch, Tabs, Typography } from 'antd';
import style from './VehicleCompatibility.module.scss';
import { addToast } from '../../features/toast/toastsSlice';
import FilterWrapper from '../../features/filterWrapper/FilterWrapper';

import { getExportFilename, getExportOptionButtons, getMMYCombination } from './VehicleModelHelper.js';
import {
	useGetDataPoolIdentifiersQuery,
	useGetVehicleModelsQuery,
	useGrouppedVehicleCompatibility
} from '../../features/vehicleModels/vehicleModelApi';
import { useCurrentCompany } from '../../features/company/companySlice.js';
import useVehicleExport from './hooks/useVehicleExport';

import { DashOutlined, FileSearchOutlined, FileTextOutlined, FilterOutlined } from '@ant-design/icons';
import VehicleCompatibilitySearchModal from './VehicleCompatibilitySearchModal.js';
import { useDispatch, useSelector } from 'react-redux';
import { setPageTitle } from '../../features/header/headerSlice';
import { ResponsiveButton } from '../../features/responsive';
import Multiselect from '../../features/multiselect/Multiselect.js';
import {
	setCategory,
	setGenericSearchEnabled,
	setPageNumber,
	setSearchFilters,
	setViewMode
} from '../../features/vehicleModels/vehicleModelFilterSlice.js';
import { VehicleMakeModelYearFormModal } from './VehicleMakeModelYearFormModal.js';
import { CsvLink } from '../../features/fileExport/CsvLink.js';
import CompatibilityTable, { VIEW_MODES } from './components/CompatibilityTable/CompatibilityTable';
import { Buttons } from '../../components/control/Buttons.js';
import { Can, useCan } from '../../features/user/Can.js';
import { FeatureFlag, Permission } from '../../features/user/Permission.js';

const { Title, Text } = Typography;

const VehicleCompatibility = () => {
	const history = useHistory();
	const { i18n, t } = useTranslation();
	const currentCompany = useCurrentCompany();
	const dispatch = useDispatch();

	const [showCsvSearch, setShowCsvSearch] = useState(false);
	const [newVehicleModelModal, setNewVehicleModelModal] = useState(false);
	const [filterForm] = Form.useForm();
	const can = useCan();

	const searchObj = useSelector(state => state.vehicleModelFilter.searchFilter);
	const categoryKey = useSelector(state => state.vehicleModelFilter.category);
	const { data: vehicleModels } = useGetVehicleModelsQuery({ companyId: currentCompany?.id }, { pollingInterval: 3600000, skip: currentCompany?.id === undefined });
    const { vehicleCompatibility, rawVehicleCompatibility, isFetching: isVehicleCompatibilityLoading } = useGrouppedVehicleCompatibility(currentCompany.id, !!history?.location?.state || !searchObj || showCsvSearch, searchObj);
	const [filterVehicleCompatibility, setFilterVehicleCompatibility] = useState(vehicleCompatibility);
	const [csvFilterVehicleCompatibility, setCsvFilterVehicleCompatibility] = useState([]);
	const { data: dataPoolIdentifiers } = useGetDataPoolIdentifiersQuery({ companyId: currentCompany?.id }, { pollingInterval: 3600000, skip: currentCompany?.id === undefined });
	const formValues = Form.useWatch([], filterForm);
	const viewMode = useSelector(state => state.vehicleModelFilter.viewMode);
	const useGenericSearch = useSelector(state => state.vehicleModelFilter.genericSearchEnabled);

	useEffect(() => {
		if (history?.location?.state) {
			filterForm.setFieldsValue(history?.location?.state);
			let obj = { ...history?.location?.state };

			if (vehicleCompatibility && history?.location?.state?.records) {
				obj.recordIds = history?.location?.state?.records;
				obj.make = searchObj?.make;
				obj.model = searchObj?.model;
				obj.year = searchObj?.year;
			}
			onFinish(obj);

			history.replace({ ...history.location, state: undefined });
		}
	}, [history?.location?.state]);

	useEffect(() => {
		dispatch(setPageTitle(t("VehicleModel.VehicleLookupTool")));

		return () => {
			dispatch(setPageTitle(""));
		}
	}, [t]);

	useEffect(() => {
		setFilterVehicleCompatibility(vehicleCompatibility);
	}, [searchObj, vehicleCompatibility, t]);

	useEffect(() => {
		if (searchObj?.recordIds !== undefined && vehicleCompatibility.length === searchObj?.recordIds?.length)
			setCsvFilterVehicleCompatibility(vehicleCompatibility);
	}, [searchObj?.recordIds, vehicleCompatibility]);

	useEffect(() => {
		if (dataPoolIdentifiers) {
			dispatch(setCategory(Object.keys(dataPoolIdentifiers)[0]));
		}
	}, [dataPoolIdentifiers])

	const onFilterChange = value => {
		filterForm.setFieldValue("recordIds", undefined);
	};

	const onCsvFilterChange = (isSupported) => {
		let obj = { ...searchObj };
		obj.isSupported = isSupported;
		filterForm.setFieldValue("isSupported", isSupported);
		onFinish(obj);
	}

	const fullValues = useMemo(() => {
		return getMMYCombination(formValues, vehicleModels, dataPoolIdentifiers);
	}, [vehicleModels,
		formValues?.year,
		formValues?.make,
		formValues?.model,
	    dataPoolIdentifiers]);

	const { csvLinkRef, csvData, handleExportExcel, handleExportCSV } = useVehicleExport({
		viewMode,
		vehicleCompatibility,
		rawVehicleCompatibility,
		dataPoolIdentifiers,
		t
	});

	const onFinish = (e) => {
		dispatch(setSearchFilters({
			...searchObj,
			...{
				make: e.make,
				model: e.model,
				year: e.year,
				q: e.q,
				isSupported: e.isSupported,
				recordIds: e.recordIds,
				measurements: e.measurements,
				time: undefined
			}
		}));
		dispatch(setPageNumber(1));
	};

	const handleAddVehicleModel = () => {
		setNewVehicleModelModal(true);
	}

	return (
		<div className={style.mainPanel}>
			<CsvLink
				data={csvData || []}
				ref={csvLinkRef}
				fileName={getExportFilename({
					entityName: t('VehicleModel.ExportFileName'),
					extension: '.csv'
				})}
			/>
			{searchObj?.recordIds && (
				<Row>
					<Col xs={{ span: 24 }} lg={{ span: 16 }} xl={{ span: 12 }}>
						<Card className={style.csvSearchPanel}>
							<Row>
								<Col xs={{ span: 4 }} className={style.centerAlign}>
									<Button
										disabled={true}
										style={{
											background: '#e2f7fc',
											borderColor: 'transparent',
											width: '60px',
											height: '60px'
										}}
										icon={<FileTextOutlined style={{ color: '#000', fontSize: '30px' }} />}
									/>
								</Col>
								<Col xs={{ span: 20 }} className={style.centerAlign}>
									<Row className={style.summaryTable}>
										<Col xs={{ span: 12 }} lg={{ span: 6 }} className={style.centerAlign}>
											<Button type="text" className={style.summaryCountBtn} disabled>
												<Text strong>{csvFilterVehicleCompatibility.length}</Text>
												<Title level={5} style={{ marginTop: 0, textAlign: 'center' }}>
													{t('VehicleModel.LoadedModel')}
												</Title>
											</Button>
										</Col>
										<Col xs={{ span: 12 }} lg={{ span: 6 }} className={style.centerAlign}>
											<Button
												type="text"
												className={style.summaryCountBtn}
												color={searchObj?.isSupported?.includes(true) === true ? "primary" : ""}
												variant={searchObj?.isSupported?.includes(true) === true ? "filled" : "text"}
												onClick={() => {
													onCsvFilterChange([true]);
												}}
											>
												<Text strong>
													{csvFilterVehicleCompatibility.filter(i => i.isSupported).length}
												</Text>
												<Title level={5} style={{ marginTop: 0, textAlign: 'center' }}>
													{t('VehicleModel.Supported')}
												</Title>
											</Button>
										</Col>
										<Col xs={{ span: 12 }} lg={{ span: 6 }} className={style.centerAlign}>
											<Button
												type="text"
												className={style.summaryCountBtn}
												color={searchObj?.isSupported?.includes(false) === true ? "primary" : ""}
												variant={searchObj?.isSupported?.includes(false) === true ? "filled" : "text"}
												onClick={() => {
													onCsvFilterChange([false]);
												}}
											>
												<Text strong>
													{csvFilterVehicleCompatibility.filter(i => i.isSupported === false).length}
												</Text>
												<Title level={5} style={{ marginTop: 0, textAlign: 'center' }}>
													{t('VehicleModel.Unsupported')}
												</Title>
											</Button>
										</Col>
										<Col xs={{ span: 12 }} lg={{ span: 6 }} className={style.centerAlign}>
											<Button
												type="text"
												className={style.summaryCountBtn}
												color={searchObj?.isSupported?.includes(-1) === true ? "primary" : ""}
												variant={searchObj?.isSupported?.includes(-1) === true ? "filled" : "text"}
												onClick={() => {
													onCsvFilterChange([-1]);
												}}
											>
												<Text strong>
													{
														csvFilterVehicleCompatibility.filter(i => i.isSupported == null)
															.length
													}
												</Text>
												<Title level={5} style={{ marginTop: 0, textAlign: 'center' }}>
													{t('VehicleModel.Unknown')}
												</Title>
											</Button>
										</Col>
									</Row>
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
			)}

			<div className={style.viewSwitcher}>
				{viewMode === VIEW_MODES.MATRIX && dataPoolIdentifiers && (
					<Tabs
					    activeKey={categoryKey}
						items={
							Object.keys(dataPoolIdentifiers).map(key => ({
								key,
								label: t('VehicleModel.MeasurementCategory.' + key),
							})).concat({
								key: 'feature',
								label: t('VehicleModel.MeasurementCategory.feature')
							})
						}
						onChange={(key) => {
							dispatch(setCategory(key))
						}}
					/>
				)}

				<Button
					type="primary"
					onClick={() => {
						dispatch(setViewMode(viewMode === VIEW_MODES.LIST ? VIEW_MODES.MATRIX : VIEW_MODES.LIST))
					}}
				>
					{viewMode === VIEW_MODES.LIST ? VIEW_MODES.MATRIX : VIEW_MODES.LIST}
				</Button>
			</div>


			<Panel noPadding style={{ marginBottom: '15px', boxShadow: 'unset' }}>
				<PanelBody>
					<Form
						form={filterForm}
						layout="inline"
						onFinish={onFinish}
						style={{ background: '#f2f2f2' }}
					>
						<FilterWrapper custom={{0: "large"}} size="medium" spacing={10} containerStyle={{ padding: 10, width: "100%" }} rightChild={<div style={{ display: "flex", justifyContent: "end" }}>
							<Space>
								<div style={{whiteSpace:"nowrap"}}>
									<Switch checked={!useGenericSearch} onChange={(e) => { dispatch(setGenericSearchEnabled(!e)) }} style={{ marginRight: 10 }} />
									{t('VehicleModel.GenericKeywordTitle')}
								</div>
								<ResponsiveButton
									loading={isVehicleCompatibilityLoading}
									onClick={() => { setShowCsvSearch(prev => !prev) }}
									title={t("VehicleModel.CsvSearch")}
									icon={<FileSearchOutlined />} />
								<Can oneOfPermission={[Permission.EXPORT, Permission.COMMON_EDIT]}>
									<Buttons placement={"bottomRight"} moreButtons={getExportOptionButtons({
										handleExportExcel, handleExportCSV, handleAddVehicleModel, can, isLoading: isVehicleCompatibilityLoading, dataSize : filterVehicleCompatibility?.length || 0
									})}
										noMargin={true}>
										<ResponsiveButton loading={isVehicleCompatibilityLoading}
											icon={<DashOutlined />} />
									</Buttons>
								</Can>
							</Space>
						</div>}>
							{useGenericSearch && <Form.Item name="q" style={{ margin: 0 }}
								rules={[
									{ required: true, message: t("ValidationErrors.KeywordRequired") },
									{
										pattern: /^[^&$!:]*$/,
										message: t("ValidationErrors.InvalidKeyword"),
									}
								]} initialValue={searchObj?.q}>
								<Input placeholder={t('VehicleModel.GenericKeyword')} />
							</Form.Item>}
							{!useGenericSearch && <Form.Item name="make" style={{ margin: 0 }} rules={[{ required: true, message: t("ValidationErrors.MakeRequired") }]} initialValue={searchObj?.make || []}>
								<Multiselect
									options={fullValues.make}
									onFilterChange={onFilterChange}
									placeholder={t('VehicleModel.Make')}
									maxCount={15}
									isLoading={isVehicleCompatibilityLoading}
								/>
							</Form.Item>}
							{!useGenericSearch && <Form.Item name="model" style={{ margin: 0 }} rules={[{ required: true, message: t("ValidationErrors.ModelRequired") }]} initialValue={searchObj?.model || []}>
								<Multiselect
									options={fullValues.model}
									onFilterChange={onFilterChange}
									maxCount={15}
									placeholder={t("VehicleModel.Model")}
									isLoading={isVehicleCompatibilityLoading} />
							</Form.Item>}
							{!useGenericSearch && <Form.Item name="year" style={{ margin: 0 }} initialValue={searchObj?.year || []}>
								<Multiselect
									options={fullValues.year}
									onFilterChange={onFilterChange}
									placeholder={t('VehicleModel.Year')}
									isLoading={isVehicleCompatibilityLoading}
								/>
							</Form.Item>}
							<Form.Item name="measurements" style={{ margin: 0 }} initialValue={searchObj?.measurements || []}>
								<Multiselect
									options={fullValues.measurements}
									onFilterChange={onFilterChange}
									placeholder={t('VehicleModel.AllMeasurement')}
									isLoading={isVehicleCompatibilityLoading}
								/>
							</Form.Item>
							<Form.Item
								name="isSupported"
								style={{ margin: 0 }}
								initialValue={searchObj?.isSupported || []}
							>
								<Multiselect
									options={fullValues.supportList}
									onFilterChange={onFilterChange}
									placeholder={t('VehicleModel.AllStatus')}
									isLoading={isVehicleCompatibilityLoading}
								/>
							</Form.Item>
							<ResponsiveButton
								loading={isVehicleCompatibilityLoading}
								title={t("Common.Apply")}
                                htmlType="submit"
								onClick={() => {
									filterForm.submit();
								}}
								icon={<FilterOutlined />} />
						</FilterWrapper>
					</Form>
					<Row className={style.tablePanel}>
						<Col xs={{ span: 24 }}>
							<CompatibilityTable
								vehicleCompatibility={filterVehicleCompatibility}
								loading={isVehicleCompatibilityLoading || vehicleCompatibility === undefined}
							/>
						</Col>
					</Row>
				</PanelBody>
			</Panel>
			<VehicleCompatibilitySearchModal
				isModalOpen={showCsvSearch}
				onComplete={(searchedData, totalRecord, matchedRecord) => {
					setShowCsvSearch(false);

					let obj = { ...searchObj };
					obj.recordIds = searchedData.map(i => i.id);
					onFinish(obj);
					filterForm.resetFields();

					dispatch(
						addToast({
							title: i18n.t('VehicleModel.CsvSearchSubmitted'),
							type: 'success'
						})
					);
				}}
				groupedModel={true}
				onClose={() => {
					setShowCsvSearch(false);
				}}
			/>
			<VehicleMakeModelYearFormModal
				isModalOpen={newVehicleModelModal}
				onClose={() => {
					setNewVehicleModelModal(false);
				}}
			/>
		</div>
	);
}

export default VehicleCompatibility;